/** Keep known positions, discard stale/duplicate IDs, append newly available modules. */
export function resolveNavigationOrder<T extends string>(
  defaultOrder: readonly T[],
  savedOrder: readonly string[],
) {
  const known = new Set<string>(defaultOrder);
  return [...new Set([...savedOrder.filter((id): id is T => known.has(id)), ...defaultOrder])];
}

export function orderNavigationItems<T extends { id: string }>(
  items: readonly T[],
  order: readonly string[],
) {
  const byId = new Map(items.map((item) => [item.id, item]));
  return resolveNavigationOrder(
    items.map((item) => item.id),
    order,
  ).flatMap((id) => {
    const item = byId.get(id);
    return item ? [item] : [];
  });
}

/** Swap visible neighbours without losing the stored positions of hidden modules. */
export function moveNavigationItem<T extends string>(
  order: readonly T[],
  visibleIds: readonly T[],
  id: T,
  direction: -1 | 1,
) {
  const visible = new Set(visibleIds);
  const orderedVisible = order.filter((candidate) => visible.has(candidate));
  const index = orderedVisible.indexOf(id);
  const neighbour = index >= 0 ? orderedVisible[index + direction] : undefined;
  if (!neighbour) return [...order];
  return order.map((candidate) =>
    candidate === id ? neighbour : candidate === neighbour ? id : candidate,
  );
}
